import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { CardState } from '../types/cardTypes';
import { PageShell } from '../components/layout/PageShell';
import { StatusPageLayout } from '../components/layout/StatusPageLayout';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import { DEV_PREVIEW_CHECKOUT_ORDER } from '../dev/devPreviewMocks';
import './Checkout.css';

// @mcc-dev-preview:start dev-preview-types
type CheckoutDevState = 'loading' | 'ready' | 'error';

const CHECKOUT_DEV_STATES: CheckoutDevState[] = ['loading', 'ready', 'error'];
// @mcc-dev-preview:end dev-preview-types

interface Order {
  id: string;
  cardId: string;
  cryptoType: string;
  amount: number;
  cryptoAmount: number;
  fee: number;
  totalCost: number;
  status: string;
  isGiftCard: boolean;
  createdAt: Date;
}

const Checkout: React.FC = () => {
  const { orderId: orderIdParam } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const devState = parseDevState(searchParams, CHECKOUT_DEV_STATES);
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrder = useCallback(async () => {
    try {
      setIsLoading(true);
      const orderData = await ApiService.getOrder(orderIdParam!);
      setOrder(orderData);
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Failed to load order details');
    } finally {
      setIsLoading(false);
    }
  }, [orderIdParam]);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    if (devState === 'loading') {
      setIsLoading(true);
      return;
    }

    setIsLoading(false);

    if (devState === 'error') {
      setOrder(null);
      setError(searchParams.get('errorMessage') ?? 'Failed to load order details');
      return;
    }

    setOrder(DEV_PREVIEW_CHECKOUT_ORDER);
    setError(null);
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;

    if (orderIdParam) {
      fetchOrder();
    }
  }, [orderIdParam, fetchOrder, devState]);

  const handlePaymentSuccess = async () => {
    if (devState) return;

    try {
      setIsProcessing(true);
      
      // Show initial success message
      // alert('Payment submitted! Processing your order...');
      
      // Poll for order status until it's completed
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds max
      const pollInterval = 1000; // 1 second intervals
      
      const pollOrderStatus = async (): Promise<void> => {
        try {
          const updatedOrder = await ApiService.getOrder(orderIdParam!);
          
          if (updatedOrder.status === 'paid' || updatedOrder.status === 'completed') {
            // Now check the card state to ensure it's been updated
            try {
              const cardData = await ApiService.getCardByCardId(order?.cardId!);
              
              if (cardData?.state === CardState.GIFT_CARD) {
                // alert('Payment successful! Your crypto will be loaded to your card shortly.');
                // navigate(`/card/${order?.cardId}?refresh=true`);
                navigate(`/thank-you?cardId=${order?.cardId}`);
                return;
              } else if (cardData?.state === CardState.READY_FOR_ACTIVATION) {
                navigate(`/redirect-to-activation?cardId=${order?.cardId}`);
                return;
              } else {
                // Continue polling for a bit longer
                attempts++;
                if (attempts >= maxAttempts) {
                  throw new Error('Card state update timeout');
                }
                setTimeout(pollOrderStatus, pollInterval);
                return;
              }
            } catch (cardError) {
              console.error('Error checking card state:', cardError);
              // If we can't check card state, proceed anyway
              alert('Payment successful! Your crypto will be loaded to your card shortly.');
              navigate(`/card/${order?.cardId}`);
              return;
            }
          }
          
          attempts++;
          if (attempts >= maxAttempts) {
            throw new Error('Order processing timeout');
          }
          
          // Continue polling
          setTimeout(pollOrderStatus, pollInterval);
        } catch (error) {
          console.error('Error polling order status:', error);
          alert('Payment submitted but there was an issue processing your order. Please contact support.');
          setIsProcessing(false);
        }
      };
      
      // Start polling
      pollOrderStatus();
      
    } catch (error) {
      console.error('Error handling payment success:', error);
      alert('Payment completed but there was an issue. Please contact support.');
      setIsProcessing(false);
    }
  };

  const handlePaymentError = (errorMessage: string) => {
    alert(`Payment failed: ${errorMessage}`);
  };

  if (isLoading) {
    return (
      <StatusPageLayout variant="loading" title="Loading order" body="Loading order details…" />
    );
  }

  if (error || !order) {
    return (
      <StatusPageLayout
        variant="error"
        title="Order not found"
        body={error || 'Order not found'}
        actions={
          <button type="button" className="ev-btn-primary" onClick={() => navigate('/purchase-crypto')}>
            Back to purchase
          </button>
        }
      />
    );
  }

  return (
    <PageShell className="checkout-container" showFooter={false} wide>
      <h1 className="ev-display-headline ev-animate-in">Complete your purchase</h1>
      <p className="ev-display-subtitle ev-animate-in">Order #{order.id}</p>

      <div className="ev-shell-card order-summary ev-animate-in">
        <h2>Order Summary</h2>
        <div className="order-details">
          <div className="detail-row">
            <span className="label">Crypto:</span>
            <span className="value">{order.cryptoType}</span>
          </div>
          <div className="detail-row">
            <span className="label">Amount:</span>
            <span className="value">${order.amount} AUD</span>
          </div>
          <div className="detail-row">
            <span className="label">Crypto Amount:</span>
            <span className="value">{order.cryptoAmount} {order.cryptoType}</span>
          </div>
          <div className="detail-row">
            <span className="label">Fee:</span>
            <span className="value">${order.fee} AUD</span>
          </div>
          <div className="detail-row total">
            <span className="label">Total:</span>
            <span className="value">${order.totalCost} AUD</span>
          </div>
        </div>
      </div>


      <div className="checkout-footer ev-animate-in">
        <button
          type="button"
          className="ev-btn-secondary"
          onClick={() => navigate('/purchase-crypto')}
          disabled={isProcessing}
        >
          Back to purchase
        </button>
      </div>
    </PageShell>
  );
};

export default Checkout; 